<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";

import { fetchAll } from "@/repositories/transactions.ts";

import { useDateRange } from "@/components/DateRange/state.ts";
import { useReservations } from "@/stores/reservations.ts";
import { useTransactions } from "@/stores/transactions.ts";

import { QueueListIcon, TableCellsIcon, ViewColumnsIcon } from "@heroicons/vue/24/outline";

import ListView from "@/components/ListView/index.vue";
import MonthlyView from "@/components/MonthlyView/index.vue";
import WeeklyView from "@/components/WeeklyView/index.vue";
import DateRange from "@/components/DateRange/index.vue";
import WeekRange from "@/components/WeekRange/index.vue";

const transactions = useTransactions();
const reservations = useReservations();
const dateRange = useDateRange();

const views = [MonthlyView, WeeklyView, ListView];

const { range, weekRange } = storeToRefs(dateRange);

const view = ref(0);

// View 1 shows a single week, fetched snapped to whole weeks; the month views load the
// window as-is. The mounted control's @refresh refires on every from/to change, including
// the ones the week arrows and the week dropdown make.
const activeRange = computed(() => (view.value === 1 ? weekRange.value : range.value));

const onLoad = async () => {
  const [resources] = await Promise.all([
    fetchAll(activeRange.value),
    reservations.load(activeRange.value),
  ]);

  transactions.fillFromResources(resources);
};

watch(view, onLoad);

onMounted(async () => onLoad());
</script>

<template>
  <div class="container px-4 flex flex-col space-y-6">
    <div class="py-3 flex flex-row justify-between items-start">
      <!-- The week view navigates by week, so the month picker gives way to the week control. -->
      <DateRange v-if="view !== 1" @refresh="onLoad"></DateRange>
      <WeekRange v-else @refresh="onLoad"></WeekRange>
      <div>
        <span class="isolate inline-flex rounded-md shadow-xs">
          <button
            class="relative -mr-px inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
            title="Per maand"
            type="button"
            @click="view = 0"
          >
            <ViewColumnsIcon class="size-3"></ViewColumnsIcon>
          </button>

          <button
            class="relative -mr-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
            title="Per week"
            type="button"
            @click="view = 1"
          >
            <TableCellsIcon class="size-3"></TableCellsIcon>
          </button>

          <button
            class="relative inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 ring-inset hover:bg-gray-50 focus:z-10"
            title="Lijst"
            type="button"
            @click="view = 2"
          >
            <QueueListIcon class="size-3"></QueueListIcon>
          </button>
        </span>
      </div>
    </div>
    <component :is="views[view]"></component>
  </div>
</template>

<style scoped></style>
